import {
  getFlowStepQuestionDefinition,
  type FlowStepDescriptor,
  type FlowStepName,
} from "@portal/app/_common/lib/flowStepTracking";
import type { HostedFlowStep } from "@portal/app/_server/services/formFunnel/payloadTypes";
import type { QuestionConfig } from "../FormFieldRenderer";
import type { FunnelStep } from "./buildFunnelSteps";
import { isQuestionVisible } from "./conditionalVisibility";

export type HostedFlowTrackingStep = HostedFlowStep | "school_match" | "clickout_handoff";
export type HostedFlowTrackingPhase = "pre_match" | "hosted";

const CHOICE_QUESTION_TYPES = new Set(["select", "radio", "pictureChoice", "multiselect"]);

function getQuestionStepName(question: QuestionConfig): FlowStepName {
  if (question.type === "email") return "email";
  if (question.type === "phone") return "contact_info";
  return getFlowStepQuestionDefinition(question.id)?.stepName ?? "question";
}

/** Configured screen positions stay fixed even when conditional screens are skipped. */
export function describeFunnelQuestionStep(
  step: FunnelStep,
  stepIndex: number,
  answers: Record<string, unknown>
): FlowStepDescriptor {
  if (step.type === "interstitial") {
    return { stepNumber: stepIndex + 1, stepName: "interstitial" };
  }
  const visibleQuestions = step.questions.filter((question) => isQuestionVisible(question, answers));
  const names = visibleQuestions.map(getQuestionStepName);
  const stepName = names.every((name) => name === names[0])
    ? (names[0] ?? "question")
    : names.every((name) => name === "email" || name === "contact_info")
      ? "contact_info"
      : "question";
  const question = visibleQuestions.length === 1 ? visibleQuestions[0] : undefined;
  // Grouped screens have no single answer. The common builder independently
  // allowlists fields and option values before serializing this input.
  const answer =
    question &&
    getFlowStepQuestionDefinition(question.id)?.allowAnswer &&
    CHOICE_QUESTION_TYPES.has(question.type) &&
    question.options?.length
      ? {
          fieldKey: question.id,
          value: answers[question.id],
          options: question.options,
        }
      : undefined;
  return {
    stepNumber: stepIndex + 1,
    stepName,
    ...(answer ? { answer } : {}),
  };
}

const ALL_HOSTED_STEPS: readonly HostedFlowStep[] = [
  "contact",
  "isUsCitizen",
  "graduatedHighSchoolIn",
  "hasRNLicense",
  "hasMilitaryAffiliation",
  "phone",
  "address",
  "addressZip",
  "dob",
  "confirm",
];

const HOSTED_STEP_NAMES: Record<HostedFlowTrackingStep, FlowStepName> = {
  contact: "contact_info",
  phone: "phone_verify",
  address: "address",
  addressZip: "address",
  dob: "question",
  graduatedHighSchoolIn: "question",
  hasMilitaryAffiliation: "question",
  hasRNLicense: "question",
  isUsCitizen: "question",
  confirm: "final_submit",
  school_match: "school_match",
  clickout_handoff: "school_match",
};

/**
 * Reserve the full configured hosted catalog, including program-dependent fields.
 * Required-field filtering or returning-user skips must not renumber later actions.
 */
export function getHostedFlowTrackingCatalog(
  configuredSteps?: readonly HostedFlowStep[]
): readonly HostedFlowStep[] {
  if (!configuredSteps?.length) return ALL_HOSTED_STEPS;
  const ordered: HostedFlowStep[] = [...new Set(configuredSteps)].filter((step) => step !== "confirm");
  if (!ordered.includes("contact")) ordered.unshift("contact");
  // deriveHostedFlowSteps inserts omitted required fields immediately after
  // contact. Reserve them in that same order, so every filtered runtime path
  // is a forward-moving subsequence of these stable analytics positions.
  const missing = ALL_HOSTED_STEPS.filter(
    (step) => step !== "confirm" && !ordered.includes(step)
  );
  ordered.splice(ordered.indexOf("contact") + 1, 0, ...missing);
  return [...ordered, "confirm"];
}

export function describeHostedFunnelStep({
  questionnaireStepCount,
  configuredSteps,
  step,
  phase = "hosted",
}: {
  questionnaireStepCount: number;
  configuredSteps?: readonly HostedFlowStep[];
  step: HostedFlowTrackingStep;
  phase?: HostedFlowTrackingPhase;
}): FlowStepDescriptor | undefined {
  if (!Number.isSafeInteger(questionnaireStepCount) || questionnaireStepCount < 0) return;
  let stepNumber: number;
  if (step === "school_match") {
    stepNumber = questionnaireStepCount + 3;
  } else if (phase === "pre_match") {
    if (step !== "contact" && step !== "phone") return;
    stepNumber = questionnaireStepCount + (step === "contact" ? 1 : 2);
  } else {
    const index = getHostedFlowTrackingCatalog(configuredSteps).indexOf(
      step === "clickout_handoff" ? "confirm" : step
    );
    if (index < 0) return;
    stepNumber = questionnaireStepCount + 4 + index;
  }
  return { stepNumber, stepName: HOSTED_STEP_NAMES[step] };
}
